// frontend/src/pages/admin/TicketPriorities.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Input, Popconfirm, Badge, Modal, Select } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';
import { useAppContext } from '../../context/AppContext';
import { useNotificationApi } from '../../context/NotificationContext';
import TicketPriorityForm from './TicketPriorityForm';

const { Search } = Input;
const { Option } = Select;

const TicketPriorities = () => {
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPriority, setEditingPriority] = useState(null);
  const [searchText, setSearchText] = useState('');

  const { appSettings } = useAppContext();
  const dateFormat = appSettings?.dateFormat || 'DD/MM/YYYY';
  const timeFormat = appSettings?.timeFormat || 'hh:mm A';

  const notificationApi = useNotificationApi();

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const fetchPriorities = useCallback(async () => {
    setLoading(true);
    const dismissLoading = showLoading('fetchPriorities', 'Loading priorities...');
    try {
      const response = await api.get('/api/priorities');
      let fetchedPriorities = response.data.data;

      const filtered = searchText
        ? fetchedPriorities.filter(priority =>
            priority.name.toLowerCase().includes(searchText.toLowerCase())
          )
        : fetchedPriorities;

      // Sort by ID descending by default
      const sorted = [...filtered].sort((a, b) => b.id - a.id);
      setPriorities(sorted);
      showSuccess('Priorities Loaded', `Successfully fetched ${sorted.length} priorities.`);
    } catch (error) {
      console.error('Failed to fetch priorities:', error);
      const errorMessage = error.response?.data?.message || 'Error fetching priorities.';
      const errorDetails = process.env.NODE_ENV === 'development' ? error.response?.data?.error : null;
      showError(
        'Loading Error',
        <span>
          {errorMessage}
          {errorDetails && <div>Details: {errorDetails}</div>}
        </span>,
        'fetchPrioritiesError'
      );
    } finally {
      setLoading(false);
      dismissLoading();
    }
  }, [searchText, showLoading, showSuccess, showError]);

  useEffect(() => {
    fetchPriorities();
  }, [fetchPriorities]);

  const handleAddPriority = () => {
    setEditingPriority(null);
    setIsModalVisible(true);
  };

  const handleEditPriority = (record) => {
    setEditingPriority(record);
    setIsModalVisible(true);
  };

  const handleDeletePriority = async (id, name) => {
    setLoading(true);
    const dismissLoading = showLoading('deletePriority', `Deleting "${name}"...`);
    try {
      await api.delete(`/api/priorities/${id}`);
      showSuccess('Priority Deleted', `Priority "${name}" deleted successfully.`);
      fetchPriorities();
    } catch (error) {
      console.error('Failed to delete priority:', error);
      const errorMessage = error.response?.data?.message || 'Error deleting priority.';
      const errorDetails = process.env.NODE_ENV === 'development' ? error.response?.data?.error : null;
      showError(
        'Deletion Failed',
        <span>
          {errorMessage}
          {errorDetails && <div>Details: {errorDetails}</div>}
        </span>,
        'deletePriorityError'
      );
    } finally {
      setLoading(false);
      dismissLoading();
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => b.id - a.id, // Default sort by ID descending
      width: 80,
    },
    {
      title: 'Priority Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      ellipsis: true,
    },
    {
      title: 'Sort Order',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      sorter: (a, b) => a.sortOrder - b.sortOrder,
      width: 120,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => dayjs(text).format(`${dateFormat} ${timeFormat}`),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      width: 180,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (text) => text ? dayjs(text).format(`${dateFormat} ${timeFormat}`) : 'N/A',
      sorter: (a, b) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix(),
      width: 180,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditPriority(record)}
            aria-label={`Edit ${record.name}`}
          />
          <Popconfirm
            title={`Are you sure you want to delete "${record.name}"?`}
            description="This action cannot be undone."
            onConfirm={() => handleDeletePriority(record.id, record.name)}
            okText="Yes, Delete"
            cancelText="No, Keep It"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Delete ${record.name}`}
            />
          </Popconfirm>
        </Space>
      ),
      width: 120,
    },
  ];

  return (
    <div className="ticket-priorities-page">
      <div className="ticket-priorities-header">
        <h2>Ticket Priority Management</h2>
        <div className="ticket-priorities-actions">
          <Search
            placeholder="Search priorities..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={fetchPriorities}
            style={{ width: 300 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddPriority}
          >
            Add Priority
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={priorities}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />

      <TicketPriorityForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={() => {
          setIsModalVisible(false);
          fetchPriorities();
        }}
        priority={editingPriority}
      />
    </div>
  );
};

export default TicketPriorities;